import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, TrendingUp, Briefcase, Database, Settings, List,
  Menu, X, Activity, LogOut, ShieldCheck, AlertOctagon, BarChart2, Receipt, Radar,
  FlaskConical, Layers
} from 'lucide-react'
import { getMarketSession, getETTime } from '../lib/utils'
import { api, schwab as schwabApi } from '../lib/api'
import { useUiMode, setUiMode, UI_MODES } from '../lib/uiMode'
import CommandPalette from './CommandPalette'
import TradingMonitorFaultModal from './TradingMonitorFaultModal'
import '../styles/responsive.css'
import { ErrorHunterButton, ErrorScanButton, NotesTool } from './HeaderTools'
import { RunningIndicator } from './RunningIndicator'
import ReminderModal from './ReminderModal'
import PreopenCheck from './PreopenCheck'
import { useAuth } from '../context/AuthContext'
import { useAccount } from '../context/AccountContext'

// 2026-10-04 (GUI audit, docs/GUI_REDESIGN_AUDIT_2026_10_04.md): eleven flat pages became four workspaces with
// sub-tabs. Routes are unchanged (every bookmark still works); only the navigation around them is new.
// Keys: 1-4 jump to a workspace, [ and ] step through its pages.
const workspaces = [
  { key: '1', name: 'Monitor',  icon: LayoutDashboard, pages: [
    { name: 'Overview', href: '/' }, { name: 'Positions', href: '/positions' }, { name: 'Portfolio', href: '/portfolio' }] },
  { key: '2', name: 'Trade',    icon: TrendingUp, pages: [
    { name: 'Signals', href: '/analysis' }, { name: 'Trades', href: '/trades' },
    { name: 'Performance', href: '/performance' }, { name: 'Gates', href: '/gates' }, { name: 'Edge', href: '/edge' }] },   // Edge Diagnostics (2026-10-14): read-only measurement page
  { key: '3', name: 'Research', icon: Radar, pages: [
    { name: 'Watchlist', href: '/watchlist' }, { name: 'Discovery', href: '/discovery' }, { name: 'Testing', href: '/testing' }, { name: 'JoshSkills', href: '/joshskills' }] },   // T-351 (was Skills, T-350): JoshSkills shadow trades + catalogue, read-only
  { key: '4', name: 'System',   icon: Database, pages: [
    { name: 'Data', href: '/data' }, { name: 'Watchers', href: '/watchers' }, { name: 'Models', href: '/models' }, { name: 'Settings', href: '/settings' }] },
  // T-195: the Trading Monitor is a dashboard page of its own, not a tab of another workspace.
  { key: '5', name: 'Tier 2 Watch', icon: Activity, pages: [{ name: 'Trading Monitor', href: '/trading-monitor' }] },
]
// The layout that was live before the 2026-10-04 redesign, kept for reference (shown with ?ui=classic).
const classicNavigation = [
  { name: 'Dashboard',  href: '/',           icon: LayoutDashboard },
  { name: 'Signals',    href: '/analysis',   icon: TrendingUp },
  { name: 'Portfolio',  href: '/portfolio',  icon: Briefcase },
  { name: 'Trades',     href: '/trades',     icon: Receipt },
  { name: 'Watchlist',  href: '/watchlist',  icon: List },
  { name: 'Discovery',  href: '/discovery',  icon: Radar },
  { name: 'Gates',       href: '/gates',       icon: ShieldCheck },
  { name: 'Performance', href: '/performance', icon: BarChart2 },
  { name: 'Testing',    href: '/testing',    icon: FlaskConical },
  { name: 'Data',       href: '/data',       icon: Database },
  { name: 'Watchers',   href: '/watchers',   icon: Radar },
  { name: 'Settings',   href: '/settings',   icon: Settings },
]
const pageActive = (pathname, href) => (href === '/' ? pathname === '/' || pathname === '/dashboard' : pathname.startsWith(href))
const workspaceIndex = (pathname) => workspaces.findIndex(w => w.pages.some(p => pageActive(pathname, p.href)))

// Schwab refresh-token rotates on every use and each rotation carries its own
// fresh 7-day lifetime (see shared/schwab_auth_manager.py::get_status()) -- so
// this countdown should keep rolling ~7 days out during normal operation and
// only actually count down toward zero if refreshes stop working. That's the
// signal worth surfacing: a shrinking countdown means something's broken.
function formatCountdown(msRemaining) {
  if (msRemaining <= 0) return 'EXPIRED'
  const totalSec = Math.floor(msRemaining / 1000)
  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const mins = Math.floor((totalSec % 3600) / 60)
  const secs = totalSec % 60
  if (days > 0) return `${days}d ${hours}h ${mins}m`
  if (hours > 0) return `${hours}h ${mins}m ${secs}s`
  return `${mins}m ${secs}s`
}

function SchwabTokenCountdown({ deadline, tokenStored, checked }) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  if (!checked) {
    return (
      <div className="flex items-center gap-2 text-slate-700">
        <span className="dot-offline" />
        <span className="hidden sm:inline tracking-wider"
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
          SCHWAB: CHECKING…
        </span>
      </div>
    )
  }

  if (!tokenStored || !deadline) {
    return (
      <div className="flex items-center gap-2 text-slate-600">
        <span className="dot-offline" />
        <span className="hidden sm:inline tracking-wider"
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
          SCHWAB TOKEN: NOT AUTHORIZED
        </span>
      </div>
    )
  }

  const deadlineMs = new Date(deadline).getTime()
  const msRemaining = deadlineMs - now
  const hoursRemaining = msRemaining / 3_600_000
  const urgency = hoursRemaining <= 0 ? 'text-rose-500'
    : hoursRemaining <= 6 ? 'text-rose-400'
    : hoursRemaining <= 48 ? 'text-amber-400'
    : 'text-emerald-400'
  const fullDate = new Date(deadline).toLocaleString('en-US', {
    dateStyle: 'medium', timeStyle: 'short',
  })

  return (
    <div className={`flex items-center gap-2 ${urgency}`}
      title={`Schwab refresh-token re-auth deadline: ${fullDate} (7 days after the last full browser login; routine token refreshes do not extend it, only a new login does)`}>
      {msRemaining <= 0 ? <span className="dot-offline" /> : <span className="dot-online" />}
      <span className="hidden sm:inline tracking-wider"
        style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
        SCHWAB {formatCountdown(msRemaining)}
      </span>
      <span className="hidden 2xl:inline text-slate-600"
        style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}>
        (until {fullDate})
      </span>
    </div>
  )
}

function LogoutButton() {
  const { logout } = useAuth()
  return (
    <button
      onClick={logout}
      title="Sign out"
      className="flex items-center gap-1.5 text-slate-700 hover:text-rose-400 transition-colors"
      style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', letterSpacing: '0.08em' }}
    >
      <LogOut className="h-3 w-3" />
      OUT
    </button>
  )
}

function Sidebar({ open, onClose }) {
  const location = useLocation()
  const mode = useUiMode()
  const classic = mode === 'orig'
  const rail = mode === 'v1'
  const isActive = (href) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href)

  // V1: icon rail, all eleven pages one click away, no labels (maximum room for the desk)
  if (rail) {
    return (
      <div className="flex flex-col h-full items-center" style={{ background: '#0d1321' }}>
        <div className="flex items-center justify-center h-14 flex-shrink-0" style={{ borderBottom: '1px solid #1e293b', width: '100%' }}>
          <Activity className="h-4 w-4 text-cyan-400" />
        </div>
        <nav className="flex-1 py-3 space-y-1 overflow-y-auto">
          {[...classicNavigation.slice(0, 3), { name: 'Positions', href: '/positions', icon: Layers }, ...classicNavigation.slice(3)].map((item) => {
            const active = pageActive(location.pathname, item.href)
            return (
              <Link key={item.name} to={item.href} onClick={onClose} title={item.name}
                className="flex items-center justify-center rounded-lg"
                style={{ width: 36, height: 36, color: active ? '#67e8f9' : '#64748b', background: active ? 'rgba(34,211,238,0.08)' : 'transparent', boxShadow: active ? 'inset 2px 0 0 rgba(34,211,238,0.65)' : 'none' }}>
                <item.icon className="h-4 w-4" />
              </Link>
            )
          })}
        </nav>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full" style={{ background: '#0d1321' }}>

      {/* Logo */}
      <div
        className="flex items-center justify-between h-14 px-5 flex-shrink-0"
        style={{
          background: 'linear-gradient(135deg, #1a2744 0%, #0d1321 100%)',
          borderBottom: '1px solid #1e293b',
        }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-md"
            style={{
              background: 'rgba(34,211,238,0.1)',
              border: '1px solid rgba(34,211,238,0.2)',
            }}
          >
            <Activity className="h-3.5 w-3.5 text-cyan-400" />
          </div>
          <span
            className="text-white font-bold tracking-widest text-sm"
            style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.15em' }}
          >
            SAFERFUNDS
          </span>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-slate-600 hover:text-slate-300 lg:hidden">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {classic && classicNavigation.map((item) => {
          const active = item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href)
          return (
            <Link key={item.name} to={item.href} onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${active ? 'text-cyan-300' : 'text-slate-500 hover:text-slate-300'}`}
              style={active ? { background: 'rgba(34,211,238,0.06)', boxShadow: 'inset 2px 0 0 rgba(34,211,238,0.65)' } : { background: 'transparent' }}>
              <item.icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-cyan-400' : 'text-slate-600'}`} />
              <span style={active ? { fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.1em', fontWeight: 600 } : { letterSpacing: '0.06em' }}>
                {item.name.toUpperCase()}
              </span>
            </Link>
          )
        })}
        {!classic && workspaces.map((ws) => {
          const active = workspaceIndex(location.pathname) === workspaces.indexOf(ws)
          return (
            <Link
              key={ws.name}
              to={active ? location.pathname : ws.pages[0].href}
              onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
                active ? 'text-cyan-300' : 'text-slate-500 hover:text-slate-300'
              }`}
              style={active ? {
                background: 'rgba(34,211,238,0.06)',
                boxShadow: 'inset 2px 0 0 rgba(34,211,238,0.65)',
              } : { background: 'transparent' }}
            >
              <ws.icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-cyan-400' : 'text-slate-600'}`} />
              <span style={active
                ? { fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.1em', fontWeight: 600 }
                : { letterSpacing: '0.06em' }}>
                {ws.name.toUpperCase()}
              </span>
              <span className="ml-auto text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem' }}>{ws.key}</span>
            </Link>
          )
        })}
      </nav>

      {/* Version + logout footer */}
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderTop: '1px solid #1e293b' }}>
        <p
          className="text-slate-700 text-xs"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          v4.3 · Paper Trading
        </p>
        <LogoutButton />
      </div>
    </div>
  )
}

export default function Layout({ children }) {
  const navigate = useNavigate()
  const loc = useLocation()
  const mode = useUiMode()
  const classic = mode === 'orig'
  const hotkeys = mode === 'v2' || mode === 'v3' || mode === 'v4'   // workspace navigation, [ ] and Ctrl+K
  const [paletteOpen, setPaletteOpen] = useState(false)
  const lastPage = React.useRef({})   // last page opened per workspace, so key 1-4 returns to where you were
  const wsIdx = workspaceIndex(loc.pathname)
  if (wsIdx >= 0) lastPage.current[wsIdx] = loc.pathname
  useEffect(() => {
    const onKey = (e) => {
      if (!hotkeys) return
      const tag = (e.target?.tagName || '').toLowerCase()
      if (['input', 'textarea', 'select'].includes(tag) || e.target?.isContentEditable || e.ctrlKey || e.metaKey || e.altKey) return
      if (e.key.length === 1 && '12345'.includes(e.key)) {
        const i = Number(e.key) - 1
        navigate(lastPage.current[i] || workspaces[i].pages[0].href)
      } else if (e.key === '[' || e.key === ']') {
        const i = workspaceIndex(window.location.pathname)
        if (i < 0) return
        const pages = workspaces[i].pages
        const cur = pages.findIndex(p => pageActive(window.location.pathname, p.href))
        navigate(pages[(cur + (e.key === ']' ? 1 : pages.length - 1)) % pages.length].href)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate, hotkeys])
  useEffect(() => {
    if (!hotkeys) return undefined
    const onK = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen(o => !o) }
    }
    window.addEventListener('keydown', onK)
    return () => window.removeEventListener('keydown', onK)
  }, [hotkeys])
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [marketSession, setMarketSession] = useState(getMarketSession())
  const [clock, setClock] = useState(getETTime())
  const [backendOk, setBackendOk] = useState(null)
  const { account, tier, bpRatioPct, minBuyingPowerPct, selectedAccount, setSelectedAccount } = useAccount()
  const [schwabAuthErr, setSchwabAuthErr] = useState(0)   // consecutive failure count
  // 2026-09-29 fix: `checked` distinguishes "haven't heard back yet" from "heard
  // back and it's genuinely not authorized" -- the initial tokenStored:false
  // default used to render as a plain "SCHWAB TOKEN: NOT AUTHORIZED" badge for
  // the ~1-2s before the first /api/schwab/status call resolves, a false alarm
  // on every single page load regardless of real token health (confirmed live:
  // reads NOT AUTHORIZED at mount, self-corrects to a healthy 6d+ countdown a
  // moment later). Real, persistent auth failures are already surfaced by the
  // separate full-width "SCHWAB AUTH FAILURE" banner below (schwabAuthErr) --
  // this small header badge doesn't need to guess during its loading window.
  const [schwabToken, setSchwabToken] = useState({ tokenStored: false, reauthDeadline: null, checked: false })
  const location = useLocation()

  const pageName = (classic || mode === 'v1' ? [...classicNavigation, { name: 'Positions', href: '/positions' }].map(n => ({ name: n.name, href: n.href })) : workspaces.flatMap(w => w.pages))
    .find(p => pageActive(location.pathname, p.href))?.name || 'Dashboard'

  // Clock tick every minute
  useEffect(() => {
    const id = setInterval(() => {
      setMarketSession(getMarketSession())
      setClock(getETTime())
    }, 60_000)
    return () => clearInterval(id)
  }, [])

  // Backend health + Schwab auth error every 60s (account data comes from AccountContext)
  useEffect(() => {
    const check = async () => {
      try {
        const health = await api.health()
        setBackendOk(true)
        // Prefer the backend's canonical session over the client-side approximation
        // (same boundaries, but the backend is the single source of truth — see
        // shared/market_hours.py — and this keeps the sidebar in sync with the
        // Dashboard page's System Status card, which already prefers it).
        if (health?.market_session) setMarketSession(health.market_session)
      } catch {
        setBackendOk(false)
      }
      try {
        const sw = await schwabApi.status()
        setSchwabAuthErr(sw?.auth_error_count || 0)
        setSchwabToken({
          tokenStored: !!sw?.token_stored,
          reauthDeadline: sw?.refresh_token_reauth_deadline || null,
          checked: true,
        })
      } catch { /* non-fatal */ }
    }
    check()
    const id = setInterval(check, 60_000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--bg-base)' }}>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-60 lg:hidden transition-transform duration-200 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:flex ${mode === 'v1' ? 'lg:w-14' : 'lg:w-60'} lg:flex-shrink-0 lg:flex-col`}>
        <Sidebar />
      </div>

      {/* Main area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

        {/* Top bar */}
        <header
          className="hdr flex items-center h-14 px-4 sm:px-6 flex-shrink-0 gap-4"
          style={{ background: '#0d1423', borderBottom: '1px solid #1e293b' }}
        >
          <button
            className="hdr-menu lg:hidden text-slate-600 hover:text-slate-300 transition-colors"
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>

          <h1
            className="hdr-title flex-1 text-slate-400 font-semibold"
            style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '0.8rem',
              letterSpacing: '0.14em',
            }}
          >
            {pageName.toUpperCase()}
          </h1>

          {/* Version switch (2026-10-04): compare the interface versions side by side over a few days */}
          <div className="hdr-ver flex items-center gap-1 mr-2" title="Interface version: ORIG is the layout that was live before the redesign">
            {UI_MODES.map(m => (
              <button key={m.key} onClick={() => setUiMode(m.key)} title={m.title}
                className="px-2 py-1 rounded"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em',
                         color: mode === m.key ? '#111' : '#94a3b8', background: mode === m.key ? '#f59e0b' : 'transparent',
                         border: `1px solid ${mode === m.key ? '#f59e0b' : '#374151'}` }}>
                {m.label}
              </button>
            ))}
          </div>

          {/* Error Hunter, Error Scan (T-358) and the notepad, in every version (2026-10-04); the running-jobs pill answers "can I deploy yet" (T-254, 2026-10-13) */}
          <div className="hdr-tools flex items-center gap-1.5 mr-2">
            <RunningIndicator />
            <ErrorHunterButton />
            <ErrorScanButton />   {/* T-358: read-only dashboard crawl */}
            <NotesTool mode={mode} page={location.pathname} />
          </div>

          {/* Account switcher (the engine's two accounts; the third, JoshSkills, is outside the engine) — MAIN and Tier 2 are both live, separate Alpaca
              accounts (2026-09-19); this picks which one the rest of the app
              (this ticker, Dashboard KPI tiles, Trades) shows. */}
          <div className="hdr-acct hidden md:flex items-center gap-1 mr-1 whitespace-nowrap">
            {[{ key: 'main', label: 'MAIN' }, { key: 'tier2', label: 'TIER 2' }].map(a => (
              <button key={a.key} onClick={() => setSelectedAccount(a.key)}
                className="px-2 py-1 rounded text-xs font-semibold transition-colors"
                style={{
                  fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', letterSpacing: '0.04em',
                  background: selectedAccount === a.key ? 'rgba(52,211,153,0.15)' : 'transparent',
                  color:      selectedAccount === a.key ? '#6ee7b7' : '#64748b',
                  border:     selectedAccount === a.key ? '1px solid rgba(52,211,153,0.4)' : '1px solid #1e293b',
                }}>
                {a.label}
              </button>
            ))}
          </div>

          {/* Account strip (portfolio value + intraday P&L) */}
          {account && (
            <div className="hdr-value hidden md:flex items-center gap-3 mr-2">
              <span className="text-slate-300 font-semibold tabular-nums"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem' }}>
                ${account.portfolio_value?.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </span>
              <span
                className={`tabular-nums font-medium ${account.intraday_pl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}
              >
                {account.intraday_pl >= 0 ? '+' : ''}
                ${account.intraday_pl?.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                {' '}({account.intraday_pl_pct >= 0 ? '+' : ''}{account.intraday_pl_pct?.toFixed(2)}%)
              </span>
              <div className="w-px h-4 bg-slate-800" />
            </div>
          )}

          {/* Status strip */}
          <div className="hdr-strip flex items-center gap-4 text-xs">

            {/* Market status — 4-state session (premarket/regular/afterhours/closed) */}
            <div className={`flex items-center gap-2 font-medium ${
              marketSession === 'regular' ? 'text-emerald-400'
                : (marketSession === 'premarket' || marketSession === 'afterhours') ? 'text-amber-400'
                : 'text-slate-600'
            }`}>
              {marketSession === 'closed'
                ? <span className="dot-offline" />
                : <span className="dot-online" />
              }
              <span
                className="hidden sm:inline tracking-wider"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}
              >
                {marketSession === 'regular' ? 'MKT OPEN'
                  : marketSession === 'premarket' ? 'PRE-MARKET'
                  : marketSession === 'afterhours' ? 'AFTER HOURS'
                  : 'MKT CLOSED'}
              </span>
              <span
                className="text-slate-700 hidden sm:inline"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}
              >
                {clock}
              </span>
            </div>

            {/* Separator */}
            <div className="w-px h-4 bg-slate-800" />

            {/* Backend status */}
            <div className={`flex items-center gap-2 ${
              backendOk === true  ? 'text-emerald-400' :
              backendOk === false ? 'text-rose-500'    : 'text-slate-600'
            }`}>
              {backendOk === true
                ? <span className="dot-online" />
                : backendOk === false
                  ? <span className="dot-offline" />
                  : <span className="dot-neutral animate-pulse" />
              }
              <span
                className="hidden sm:inline tracking-wider"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}
              >
                {backendOk === true ? 'API OK' : backendOk === false ? 'API ERR' : '· · ·'}
              </span>
            </div>

            {/* Separator */}
            <div className="w-px h-4 bg-slate-800" />

            {/* Schwab refresh-token re-auth countdown — see SchwabTokenCountdown above */}
            <SchwabTokenCountdown
              deadline={schwabToken.reauthDeadline}
              tokenStored={schwabToken.tokenStored}
              checked={schwabToken.checked}
            />
          </div>
        </header>

        {/* CRITICAL: Schwab auth failure banner — shown on every page */}
        {schwabAuthErr > 0 && (
          <div
            className="flex items-center gap-3 px-5 py-2.5 flex-shrink-0"
            style={{
              background: 'rgba(185,28,28,0.18)',
              borderBottom: '2px solid rgba(220,38,38,0.7)',
              boxShadow: '0 2px 16px rgba(220,38,38,0.2)',
            }}
          >
            <AlertOctagon className="h-4 w-4 text-red-400 flex-shrink-0 animate-pulse" />
            <span className="flex-1 text-red-300 font-bold"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.08em' }}>
              SCHWAB AUTH FAILURE &nbsp;&mdash;&nbsp; REAL-TIME DATA OFFLINE
              {schwabAuthErr > 1 && ` (${schwabAuthErr} CONSECUTIVE FAILURES)`}
              &nbsp;&nbsp;·&nbsp;&nbsp; TOKEN REFRESH RUNNING AUTOMATICALLY
            </span>
            <a
              href="/settings"
              className="flex-shrink-0 px-3 py-1 rounded font-bold"
              style={{
                background: 'rgba(220,38,38,0.3)',
                border: '1px solid rgba(220,38,38,0.6)',
                color: '#fca5a5',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.62rem',
                letterSpacing: '0.08em',
                whiteSpace: 'nowrap',
              }}
            >
              RE-AUTHORIZE
            </a>
          </div>
        )}

        {/* Capital tier banner — RED blocks all new BUYs (sf_signal_scanner.py ~line 3661) */}
        {tier === 'RED' && (
          <div
            className="flex items-center gap-3 px-5 py-2.5 flex-shrink-0"
            style={{
              background: 'rgba(185,28,28,0.18)',
              borderBottom: '2px solid rgba(220,38,38,0.7)',
              boxShadow: '0 2px 16px rgba(220,38,38,0.2)',
            }}
          >
            <AlertOctagon className="h-4 w-4 text-red-400 flex-shrink-0" />
            <span className="flex-1 text-red-300 font-bold"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.08em' }}>
              CAPITAL RED TIER &nbsp;&mdash;&nbsp; NEW BUYS BLOCKED
              &nbsp;&nbsp;·&nbsp;&nbsp; buying power {bpRatioPct?.toFixed(1)}% of portfolio, below the {minBuyingPowerPct?.toFixed(0)}% floor
              &nbsp;&nbsp;·&nbsp;&nbsp; SELLs unaffected, floor drops to 0.3% profit to free capital faster
            </span>
          </div>
        )}

        <ReminderModal />
        <TradingMonitorFaultModal />

        {/* Workspace sub-tabs */}
        {hotkeys && wsIdx >= 0 && workspaces[wsIdx].pages.length > 1 && (
          <div className="subtabs flex items-center gap-1 px-4 sm:px-6 py-1.5 flex-shrink-0" style={{ borderBottom: '1px solid #1e293b', background: '#0d1321' }}>
            {workspaces[wsIdx].pages.map(p => {
              const on = pageActive(loc.pathname, p.href)
              return (
                <Link key={p.href} to={p.href} className="px-3 py-1 rounded text-xs"
                  style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', color: on ? '#67e8f9' : '#64748b',
                           background: on ? 'rgba(34,211,238,0.08)' : 'transparent', border: `1px solid ${on ? 'rgba(34,211,238,0.35)' : 'transparent'}` }}>
                  {p.name.toUpperCase()}
                </Link>
              )
            })}
            <span className="flex-1" />
            {/* T-365 [LIVE 2026-11-04 shots]: the 4 px row gap left the button touching the keys hint on every System tab */}
            {workspaces[wsIdx].name === 'System' && <span className="mr-3"><PreopenCheck /></span>}
            <span className="text-slate-700 hidden md:inline" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem' }}>keys: 1-4 workspace · [ ] page · Ctrl+K jump</span>
          </div>
        )}

        {hotkeys && <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} pages={workspaces.flatMap(w => w.pages.map(p => ({ label: `${w.name}  ·  ${p.name}`, href: p.href })))} />}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className={mode === 'v1' ? 'p-2 h-full' : 'p-4 sm:p-6 max-w-screen-2xl mx-auto'}>
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
